import React, { useMemo, useState, useEffect } from 'react';
import { apiClient } from '../../services/apiClient';
import { DataListTable, type Column } from '../../components/crm/DataListTable';

interface PricingPlan {
  id?: string;
  name: string;
  price: number;
  billing_period: 'monthly' | 'annual';
  features: string[];
  max_users: number;
  max_leads: number;
  is_active: boolean;
}

interface TenantSubscription {
  tier: string;
  plan: string;
  status: string;
  price: number;
  billing_period: string;
  current_period_end: string | null;
}

interface Tenant {
  id: string;
  name: string;
  domain: string;
  users_count: number;
  subscription: TenantSubscription | null;
}

const PricingManagement: React.FC = () => {
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchPlans(), fetchTenants()]);
      setLoading(false);
    };
    load();
  }, []);

  const fetchPlans = async () => {
    try {
      setError('');
      const data = await apiClient.get('/pricing/plans/');
      const list = Array.isArray(data) ? data : (data?.results ?? data?.plans ?? []);
      setPlans(list);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch pricing plans');
    }
  };

  const fetchTenants = async () => {
    try {
      const data = await apiClient.get('/admin/tenants/');
      setTenants(Array.isArray(data) ? data : []);
    } catch {
      setTenants([]);
    }
  };

  const tenantColumns = useMemo<Column<Tenant>[]>(
    () => [
      {
        id: 'tenant',
        header: 'Tenant',
        mobilePriority: 'primary',
        mobileLabel: 'Tenant',
        render: (t) => <span className="text-sm font-medium text-gray-900">{t.name}</span>,
      },
      {
        id: 'users',
        header: 'Users',
        mobileLabel: 'Users',
        render: (t) => <span className="text-sm text-gray-600">{t.users_count}</span>,
      },
      {
        id: 'tier',
        header: 'Tier',
        mobileLabel: 'Tier',
        render: (t) => (
          <span className="text-sm">{t.subscription?.tier?.toUpperCase() ?? '-'}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (t) => <span className="text-sm">{t.subscription?.status ?? '-'}</span>,
      },
      {
        id: 'price',
        header: 'Price',
        mobileLabel: 'Price',
        render: (t) => (
          <span className="text-sm">
            {t.subscription
              ? `$${t.subscription.price}/${t.subscription.billing_period === 'monthly' ? 'mo' : 'yr'}`
              : '-'}
          </span>
        ),
      },
      {
        id: 'period_end',
        header: 'Period End',
        mobileLabel: 'Period End',
        render: (t) => (
          <span className="text-sm text-gray-600">
            {t.subscription?.current_period_end
              ? new Date(t.subscription.current_period_end).toLocaleDateString()
              : '-'}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Pricing Plans</h2>
            <p className="text-sm text-gray-500 mt-1">Platform plans are managed by the system. View-only.</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Current Tenants & Subscriptions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Tenants & Subscriptions</h3>
        <DataListTable<Tenant>
          columns={tenantColumns}
          rows={tenants}
          getRowKey={(t) => t.id}
          loading={loading}
          emptyContent={
            <p className="text-sm text-gray-500 mt-4">No tenants yet.</p>
          }
        />
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                {!plan.is_active && (
                  <span className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 rounded">
                    Inactive
                  </span>
                )}
              </div>

              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                <span className="text-gray-500">/{plan.billing_period === 'monthly' ? 'mo' : 'yr'}</span>
              </div>

              <div className="space-y-2 mb-6">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Users:</span>{' '}
                  {plan.max_users === -1 ? 'Unlimited' : plan.max_users}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Leads:</span>{' '}
                  {plan.max_leads === -1 ? 'Unlimited' : plan.max_leads.toLocaleString()}
                </div>
              </div>

              <div className="space-y-2 mb-6">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start text-sm text-gray-600">
                    <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PricingManagement;
